import { gql, useMutation, useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'

import {
  ApplyResaleItemRaffleMutation,
  ApplyResaleItemRaffleMutationVariables,
  ItemDetailBoxFragmentDoc,
  PaymentDetailFragmentDoc,
  ResaleApplicationCreateConfirmationQuery,
  ResaleApplicationCreateConfirmationQueryVariables,
} from '@/clients/utoniq-core/schema'
import { useMessage, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { getResaleApplicationInput } from '@/core/native/storage'
import { decodeSkuId, setItemSku } from '@/core/sku'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, generatePath, routerPages } =
    useRouter<{ itemId: string; skuId?: string }>()
  const itemId = router.query.itemId
  const skuId = router.query?.skuId

  const { showError } = useMessage()
  const [itemCount, setItemCount] = useState(0)

  const { data } = useQuery<
    ResaleApplicationCreateConfirmationQuery,
    ResaleApplicationCreateConfirmationQueryVariables
  >(QUERY, {
    variables: {
      id: itemId,
      skuId: decodeSkuId(skuId ?? '') || undefined,
    },
  })

  const [applyResaleItemRaffle, { loading: submitting }] = useMutation<
    ApplyResaleItemRaffleMutation,
    ApplyResaleItemRaffleMutationVariables
  >(APPLY_RESALE_ITEM_RAFFLE)

  useEffect(() => {
    ;(async () => {
      const v = await getResaleApplicationInput()
      setItemCount(Number(v.itemCount))
    })()
  }, [getResaleApplicationInput])

  return {
    state: {
      cardNumber: data?.paymentSource?.last4,
      item: setItemSku(data?.item ?? null, skuId),
      brand: data?.paymentSource?.brand,
      selectedCount: itemCount,
      submitting,
    },
    actions: {
      onSubmit: async () => {
        applyResaleItemRaffle({
          variables: {
            inputs: {
              itemId,
              itemCount,
              skuId: skuId ? decodeSkuId(skuId) : null,
            },
          },
        })
          .then(({ data }) => {
            router.push({
              pathname: generatePath(
                routerPages.ResaleApplicationCreateCompletion,
                {
                  itemId,
                  raffleId: data?.applyResaleItemRaffle.id,
                }
              ),
              search: `${skuId ? `?skuId=${skuId}` : ''}`,
            })
          })
          .catch(showError)
      },
    },
  }
})

const QUERY = gql`
  query ResaleApplicationCreateConfirmation($id: ID!, $skuId: String) {
    item(id: $id) {
      ...ItemDetailBox
    }
    paymentSource {
      ...PaymentDetail
    }
  }
  ${PaymentDetailFragmentDoc}
  ${ItemDetailBoxFragmentDoc}
`

const APPLY_RESALE_ITEM_RAFFLE = gql`
  mutation ApplyResaleItemRaffle($inputs: ApplyResaleItemRaffleInput!) {
    applyResaleItemRaffle(inputs: $inputs) {
      id
    }
  }
`
